import _defineProperty from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _classCallCheck from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/home/runner/work/website/website/node_modules/@babel/runtime/helpers/esm/createClass.js";
var _excluded = ["autoplay", "delay", "type", "repeat", "repeatDelay", "repeatType"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { time } from '../../frameloop/sync-time.mjs';
import { flushKeyframeResolvers } from '../../render/utils/KeyframesResolver.mjs';
import { instantAnimationState } from '../../utils/use-instant-transition-state.mjs';
import { canAnimate } from './utils/can-animate.mjs';
import { getFinalKeyframe } from './waapi/utils/get-final-keyframe.mjs';

/**
 * Maximum time allowed between an animation being created and it being
 * resolved for us to use the latter as the start time.
 *
 * This is to ensure that while we prefer to "start" an animation as soon
 * as it's triggered, we also want to avoid a visual jump if there's a big delay
 * between these two moments.
 */
var MAX_RESOLVE_DELAY = 40;
var BaseAnimation = /*#__PURE__*/function () {
  function BaseAnimation(_ref) {
    var _ref$autoplay = _ref.autoplay,
      autoplay = _ref$autoplay === void 0 ? true : _ref$autoplay,
      _ref$delay = _ref.delay,
      delay = _ref$delay === void 0 ? 0 : _ref$delay,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? "keyframes" : _ref$type,
      _ref$repeat = _ref.repeat,
      repeat = _ref$repeat === void 0 ? 0 : _ref$repeat,
      _ref$repeatDelay = _ref.repeatDelay,
      repeatDelay = _ref$repeatDelay === void 0 ? 0 : _ref$repeatDelay,
      _ref$repeatType = _ref.repeatType,
      repeatType = _ref$repeatType === void 0 ? "loop" : _ref$repeatType,
      options = _objectWithoutProperties(_ref, _excluded);
    _classCallCheck(this, BaseAnimation);
    // Track whether the animation has been stopped. Stopped animations won't restart.
    this.isStopped = false;
    this.hasAttemptedResolve = false;
    this.createdAt = time.now();
    this.options = _objectSpread({
      autoplay: autoplay,
      delay: delay,
      type: type,
      repeat: repeat,
      repeatDelay: repeatDelay,
      repeatType: repeatType
    }, options);
    this.updateFinishedPromise();
  }
  /**
   * This method uses the createdAt and resolvedAt to calculate the
   * animation startTime. *Ideally*, we would use the createdAt time as t=0
   * as the following frame would then be the first frame of the animation in
   * progress, which would feel snappier.
   *
   * However, if there's a delay (main thread work) between the creation of
   * the animation and the first commited frame, we prefer to use resolvedAt
   * to avoid a sudden jump into the animation.
   */
  return _createClass(BaseAnimation, [{
    key: "calcStartTime",
    value: function calcStartTime() {
      if (!this.resolvedAt) return this.createdAt;
      return this.resolvedAt - this.createdAt > MAX_RESOLVE_DELAY ? this.resolvedAt : this.createdAt;
    }
    /**
     * A getter for resolved data. If keyframes are not yet resolved, accessing
     * this.resolved will synchronously flush all pending keyframe resolvers.
     * This is a deoptimisation, but at its worst still batches read/writes.
     */
  }, {
    key: "resolved",
    get: function get() {
      if (!this._resolved && !this.hasAttemptedResolve) {
        flushKeyframeResolvers();
      }
      return this._resolved;
    }
    /**
     * A method to be called when the keyframes resolver completes. This method
     * will check if its possible to run the animation and, if not, skip it.
     * Otherwise, it will call initPlayback on the implementing class.
     */
  }, {
    key: "onKeyframesResolved",
    value: function onKeyframesResolved(keyframes, finalKeyframe) {
      this.resolvedAt = time.now();
      this.hasAttemptedResolve = true;
      var _this$options = this.options,
        name = _this$options.name,
        type = _this$options.type,
        velocity = _this$options.velocity,
        delay = _this$options.delay,
        onComplete = _this$options.onComplete,
        onUpdate = _this$options.onUpdate,
        isGenerator = _this$options.isGenerator;
      /**
       * If we can't animate this value with the resolved keyframes
       * then we should complete it immediately.
       */
      if (!isGenerator && !canAnimate(keyframes, name, type, velocity)) {
        // Finish immediately
        if (instantAnimationState.current || !delay) {
          onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate(getFinalKeyframe(keyframes, this.options, finalKeyframe));
          onComplete === null || onComplete === void 0 ? void 0 : onComplete();
          this.resolveFinishedPromise();
          return;
        }
        // Finish after a delay
        else {
          this.options.duration = 0;
        }
      }
      var resolvedAnimation = this.initPlayback(keyframes, finalKeyframe);
      if (resolvedAnimation === false) return;
      this._resolved = _objectSpread({
        keyframes: keyframes,
        finalKeyframe: finalKeyframe
      }, resolvedAnimation);
      this.onPostResolved();
    }
  }, {
    key: "onPostResolved",
    value: function onPostResolved() {}
    /**
     * Allows the returned animation to be awaited or promise-chained. Currently
     * resolves when the animation finishes at all but in a future update could/should
     * reject if its cancels.
     */
  }, {
    key: "then",
    value: function then(resolve, reject) {
      return this.currentFinishedPromise.then(resolve, reject);
    }
  }, {
    key: "updateFinishedPromise",
    value: function updateFinishedPromise() {
      var _this = this;
      this.currentFinishedPromise = new Promise(function (resolve) {
        _this.resolveFinishedPromise = resolve;
      });
    }
  }]);
}();
export { BaseAnimation };